import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { useState } from 'react'

interface AppTooltipProps {
  content?: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  children: React.ReactNode
}

export const AppTooltip: React.FC<AppTooltipProps> = ({ content, position = 'top', children }) => {
  const [open, setOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: position,
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role])

  if (!content) return <>{children}</>

  const child = React.Children.only(children) as React.ReactElement<Record<string, unknown>>
  const cloned = React.cloneElement(
    child,
    getReferenceProps({ ref: refs.setReference, ...child.props })
  )

  return (
    <>
      {cloned}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 rounded bg-[#212529] px-3 py-2 text-sm text-white"
          >
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
